/**
 * The privacy policy and the terms, as pages a person can actually read.
 *
 * PRIVACY.md is the engineering contract: what the code may and may not do.
 * This is the other half of the same promise -- the version the people
 * affected by it are entitled to see. The two must not drift apart, so the
 * substantive claims here (first names only, {NAME_RETENTION_DAYS}-day
 * deletion, no third-party tracking, no card data) are the same claims the
 * code is built to keep, and docs/COMPLIANCE.md maps each one to the
 * obligation it satisfies.
 *
 * Structure follows who is affected, not who is paying. The golfer section
 * comes first and is written for a parent standing on a tee box with a phone,
 * because they are the person with the least choice in the matter: a venue
 * read a contract and signed it, a nine-year-old did not.
 *
 * Neither document has been reviewed by a lawyer. See src/site/legal.ts.
 */

import { LEGAL, NAME_RETENTION_DAYS } from "./legal-details";
import { Footer, Nav } from "./Shell";
import "./site.css";

function LegalPage({
  eyebrow,
  title,
  lede,
  children,
}: {
  eyebrow: string;
  title: string;
  lede: string;
  children: React.ReactNode;
}) {
  return (
    <div className="site">
      <div className="site__wrap">
        <Nav page="legal" />
        <section className="site-section" style={{ paddingTop: 48 }}>
          <p className="site-eyebrow">{eyebrow}</p>
          <h1 className="site-h2">{title}</h1>
          <p className="site-lede">{lede}</p>
          <p className="site-note">Effective {LEGAL.effectiveDate}.</p>
          <div className="site-legal">{children}</div>
        </section>
        <Footer />
      </div>
    </div>
  );
}

export function Privacy() {
  return (
    <LegalPage
      eyebrow="Privacy"
      title="Privacy Policy"
      lede="Two different kinds of people appear in this system, and they are owed
        different explanations. Golfers come first, because they never signed
        anything."
    >
      <h2>Part 1: If you played a round</h2>

      <p className="site-p">
        This section is for golfers and for the parents of children who played.
        You did not create an account, you were not asked to agree to anything,
        and you should not have to read a contract to find out what happened to
        your child&rsquo;s name. So, plainly:
      </p>

      <h3>What is collected</h3>
      <ul>
        <li>
          <strong>A first name or a nickname</strong>, typed by whoever set up
          the scorecard. A nickname works exactly as well and we say so on the
          screen where the name is entered.
        </li>
        <li>
          <strong>The scores for that round</strong>: strokes per hole,
          and which course it was played on.
        </li>
        <li>
          <strong>A temporary code stored on the phone</strong> so that closing
          the browser mid-round does not lose the round. It belongs to that one
          round at that one venue, is not linked to a person, is not reused
          anywhere else, and is not used to recognise a device later.
        </li>
      </ul>

      <h3>What is never collected</h3>
      <p className="site-p">
        No last names. No email addresses, phone numbers, birthdates or ages. No
        photographs, audio or video. No location beyond which course the round
        was played on. There is no golfer account to create and no way to create
        one, by design.
      </p>

      <h3>Where the name appears</h3>
      <p className="site-p">
        On the television screens at the venue, for the duration of the round
        and while the finished-round board still shows it. That is the entire
        point of the product and it is the one thing to understand before
        entering a name: it is a public screen in a public place. If that is not
        wanted, a nickname gives the same game with none of the exposure.
      </p>

      <h3>How long it is kept</h3>
      <p className="site-p">
        Names are permanently deleted <strong>{NAME_RETENTION_DAYS} days</strong>{" "}
        after the round finishes: the player names, the team name, and
        the temporary code on the phone. Removed from the record, not hidden.
        The scores stay, with nobody&rsquo;s name attached, so a venue can keep
        a course record. This runs as a scheduled job and the system reports
        its own health, because a deletion rule that quietly stops running is
        worse than never having had one.
      </p>
      <p className="site-p">
        One honest exception: encrypted backups. We keep two weeks of them so
        a failed server does not lose a venue&rsquo;s season, which means a
        name deleted today can still exist in a backup for up to two weeks
        afterwards before ageing out. Those backups are encrypted and are
        never read except to restore the system. Our longer-term archives
        contain no player data at all.
      </p>

      <h3>Children</h3>
      <p className="site-p">
        Children play mini golf, so children&rsquo;s first names go on these
        screens. The system is deliberately built so that what is held about a
        child is as close to nothing as a scoreboard allows: a first name or
        nickname with no surname, no contact details, no age, no persistent
        identifier that could follow them anywhere, no advertising or analytics
        of any kind, and automatic deletion after {NAME_RETENTION_DAYS} days.
      </p>
      <p className="site-p">
        We do not knowingly collect anything beyond that from a child, and there
        is no mechanism in the product by which we could. If you believe
        something more was collected about your child, write to{" "}
        <strong>{LEGAL.privacyEmail}</strong> and it will be deleted.
      </p>

      <h3>Removing a name sooner</h3>
      <p className="site-p">
        Ask the venue, or write to <strong>{LEGAL.privacyEmail}</strong> with
        the venue and the approximate date and time of the round. A name can be
        removed immediately on request. There is no need to wait out the{" "}
        {NAME_RETENTION_DAYS} days, and no need to explain why.
      </p>

      <h2>Part 2: If you run a venue</h2>

      <p className="site-p">
        Venue staff have accounts, which is a different kind of relationship and
        a different set of rules.
      </p>

      <h3>What is held</h3>
      <ul>
        <li>An email address, and optionally a display name.</li>
        <li>
          A password hash: Argon2id, never the password itself, which is
          not recoverable by us or by anyone else.
        </li>
        <li>When the account last signed in.</li>
        <li>
          Your venue&rsquo;s own configuration: course names, hole pars,
          branding, sponsor images.
        </li>
      </ul>
      <p className="site-p">
        <strong>Your sign-ins are logged.</strong> Each session records the IP
        address it came from, the browser and device it used, when it started
        and when it was last used. You can see all of it for your own account,
        and end any session you do not recognise. We keep sign-in history for
        90 days and then delete it.
      </p>
      <p className="site-p">
        That is the opposite of how golfers are treated above, and the
        difference is deliberate. You have an account and agreed to terms, and
        being able to look at a list and say &ldquo;that one is not me&rdquo;
        is the whole point of having one. A golfer agreed to nothing and is
        often a child, so nothing of the sort is recorded about them.
      </p>

      <h3>Payment</h3>
      <p className="site-p">
        Card details never reach our servers. Checkout and billing management
        are hosted by Stripe, and the browser goes to Stripe directly.
        There is no card field anywhere in this application and no endpoint that
        would accept one. We receive confirmation that a subscription is active,
        which plan it is on, and when the period ends. We never see a card
        number, and we could not store one if we wanted to.
      </p>

      <h3>How long it is held</h3>
      <p className="site-p">
        For as long as the account is in use, and then deleted on request or
        when the venue&rsquo;s data is deleted. The{" "}
        {NAME_RETENTION_DAYS}-day rule above is about golfers, who never asked
        for an account; it would make no sense to apply it to a manager who
        needs the same login next season.
      </p>

      <h2>Part 3: Applies to everyone</h2>

      <h3>Who is responsible for what</h3>
      <p className="site-p">
        For golfer data, the venue decides to run the boards and we act on its
        instructions. In GDPR terms the venue is the controller and we
        are its processor. For venue staff accounts and billing, we are the
        controller. Venues subject to the GDPR or UK GDPR should have a data
        processing agreement with us; ask at {LEGAL.privacyEmail} and one will
        be provided.
      </p>

      <h3>Who else sees the data</h3>
      <p className="site-p">
        Deliberately almost nobody. There are{" "}
        <strong>
          no analytics, no advertising networks, no tracking pixels, no session
          recorders and no tag managers
        </strong>{" "}
        on the golfer scorecard or anywhere else. This is enforced by a
        Content-Security-Policy rather than left to good intentions, so a
        dependency that quietly starts calling home breaks visibly instead of
        working silently. Sponsor images are served from our own storage, not
        from an ad network. The only third parties involved at all are Stripe,
        for payment, and the hosting provider that runs the servers.
      </p>

      <h3>International transfers</h3>
      <p className="site-p">
        The servers are in the United States. A venue in the EU or the UK whose
        golfers&rsquo; data is processed here is relying on Standard Contractual
        Clauses, which form part of the data processing agreement above. We have
        not appointed an Article 27 representative in the Union, because we do
        not yet serve a venue that requires one; if you are that venue, say so
        before signing and it will be arranged rather than glossed over.
      </p>

      <h3>Your rights</h3>
      <p className="site-p">
        Depending on where you live, you may have the right to see what is held
        about you, to correct it, to have it deleted, to object to it being
        processed, or to receive a copy. Write to{" "}
        <strong>{LEGAL.privacyEmail}</strong>. There is no charge, and we do not
        require a reason.
      </p>
      <p className="site-p">
        We do not sell personal information and we do not share it for
        cross-context behavioural advertising, under the meaning the California
        Consumer Privacy Act gives those terms or under any other. There is no
        opt-out link below this paragraph because there is nothing to opt out
        of. There is no profiling and no automated decision-making.
      </p>

      <h3>Security</h3>
      <p className="site-p">
        Everything travels over TLS. Passwords are hashed with Argon2id. The
        database is not reachable from the public internet. Backups are
        encrypted and kept off the server. No part of the product runs with more
        privilege than it needs.
      </p>

      <h3>Changes</h3>
      <p className="site-p">
        If this policy changes in a way that matters, the effective date at the
        top changes with it and venues are told by email. We will not quietly
        start collecting something this document says we do not.
      </p>

      <h3>Contact</h3>
      <p className="site-p">
        Mini Golf Scoreboard is a product of {LEGAL.brand}, a trading name of{" "}
        {LEGAL.entity}. {LEGAL.entity} is the company responsible for your
        data and the one you are contracting with.
      </p>
      <p className="site-p">
        {LEGAL.entity}
        <br />
        {LEGAL.address}
        <br />
        {LEGAL.privacyEmail}
      </p>
    </LegalPage>
  );
}

export function Terms() {
  return (
    <LegalPage
      eyebrow="Terms"
      title="Terms of Service"
      lede="The agreement between us and the venue that subscribes. Golfers are not
        party to it and are not asked to agree to anything."
    >
      <h3>1. What the service is</h3>
      <p className="site-p">
        A live scoring system for mini golf: golfers scan a QR code and enter
        strokes on their own phones, and every group currently playing appears
        on screens at your venue. We provide it as a hosted service; you provide
        the course, the screens and the network.
      </p>

      <h3>2. Who may subscribe</h3>
      <p className="site-p">
        A business operating a mini golf course or similar venue, through
        somebody authorised to bind it. You are responsible for what happens
        under your account, including what your staff do with it.
      </p>

      <h3>3. Subscription and payment</h3>
      <p className="site-p">
        Plans are billed monthly in advance through Stripe, at the price shown
        on the pricing page when you subscribe. The subscription renews each
        month until cancelled. You can cancel at any time from your own admin,
        which takes effect at the end of the period you have already paid for.
        We will give notice before any price change takes effect for you.
      </p>
      <p className="site-p">
        <strong>Refunds.</strong> If the service is not what you need, ask
        within <strong>30 days of your first payment</strong> and we will
        refund it in full. After that first 30 days we do not refund, including
        for a month already begun. Cancelling stops the next charge
        rather than reversing the last one. Cancel whenever you like; the
        boards keep working until the period you have paid for runs out.
      </p>

      <h3>4. What you are responsible for</h3>
      <ul>
        <li>
          <strong>Telling golfers what happens.</strong> The product shows a
          notice before any name is entered, and your staff should not talk
          anybody past it. Names appear on a public screen.
        </li>
        <li>
          <strong>Your QR codes.</strong> Your venue address is printed on them
          and cannot be changed afterwards without reprinting.
        </li>
        <li>
          <strong>Sponsor content you upload.</strong> You need the right to
          display it, and it must be suitable for a family venue.
        </li>
        <li>
          <strong>Not misusing the service</strong>: no attempts to break
          into it, overload it, scrape it, or use it to collect information
          about golfers beyond what it is designed to show.
        </li>
      </ul>

      <h3>5. What we are responsible for</h3>
      <p className="site-p">
        Running the service with reasonable care and keeping to the privacy
        policy. We do not currently offer a contractual uptime guarantee, and we
        would rather say so than publish a number we are not prepared to pay out
        against. The product is built so that a server outage degrades to
        &ldquo;the television is stale&rdquo; rather than &ldquo;scoring is
        broken&rdquo;: strokes are stored on the phone first and sync afterwards.
      </p>

      <h3>6. Data</h3>
      <p className="site-p">
        Your configuration and your venue&rsquo;s scoring data are yours. We
        process golfer data on your instructions as your processor, under the
        privacy policy and any data processing agreement between us. Golfer
        names are deleted after {NAME_RETENTION_DAYS} days automatically and
        this is not configurable, including by you. It is the basis on
        which golfers are told their names are used.
      </p>

      <h3>7. Intellectual property</h3>
      <p className="site-p">
        The software is ours and stays ours. Your brand, logo and sponsor
        creative stay yours; you grant us only the permission needed to display
        them in your own boards and scorecards.
      </p>

      <h3>8. Ending the agreement</h3>
      <p className="site-p">
        You may cancel at any time. We may suspend or end an account for
        non-payment, or for use that breaks section 4, with notice where it is
        reasonable to give it. On termination your boards stop and your data is
        deleted on request; tell us before you cancel if you want an export.
      </p>

      <h3>9. Disclaimers and liability</h3>
      <p className="site-p">
        The service is provided as it is, without warranties beyond those that
        cannot be excluded by law. We are not liable for indirect or
        consequential loss, or for lost profits. Our total liability to you for
        any claim is capped at{" "}
        <strong>
          the total amount you paid us in the twelve months before the claim
          arose
        </strong>
        . Nothing here limits liability that the law does not permit to be
        limited, which includes fraud, willful misconduct, and anything
        else Florida law puts beyond the reach of a clause like this one.
      </p>

      <h3>10. Governing law</h3>
      <p className="site-p">
        These terms are governed by the laws of {LEGAL.governingLaw}, and
        disputes belong to the courts there.
      </p>

      <h3>11. Changes</h3>
      <p className="site-p">
        We may update these terms; material changes are notified by email before
        they take effect, and continuing to use the service afterwards is
        acceptance. If a change is unacceptable, cancel before it takes effect.
      </p>

      <h3>12. Contact</h3>
      <p className="site-p">
        Your agreement is with <strong>{LEGAL.entity}</strong>, which trades as{" "}
        {LEGAL.brand} and publishes Mini Golf Scoreboard. {LEGAL.brand} is a
        brand rather than a company; it is not a party to these terms.
      </p>
      <p className="site-p">
        {LEGAL.entity}
        <br />
        {LEGAL.address}
        <br />
        {LEGAL.supportEmail}
      </p>
    </LegalPage>
  );
}
